// Guardia de la sesión SIN cuenta.
//
// Firebase crea la sesión en cuanto alguien entra con Google, pero la cuenta de
// MekaFix la crea el paso siguiente: elegir el tipo de cuenta, que es quien
// llama a `POST /users` y escribe la fila en `app_user`. Quien abandona entre
// una cosa y la otra queda con sesión y sin cuenta — y al volver a abrir la app
// entraba directo al home.
//
// Desde ahí la app le enseña su nombre y su avatar (los da Google, no nosotros),
// o sea le asegura que su cuenta existe, mientras el servidor responde 404 a
// todo lo que lee y **500** a lo primero que intenta guardar: en Postgres el
// carro cuelga de la persona por clave foránea, así que sin fila no hay de quién
// colgarlo. Pasó en producción el 2026-08-16 con dos cuentas.
//
// Esta guardia le devuelve al único sitio que puede arreglarlo, en vez de
// dejarle usar una app que no puede guardarle nada.
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useThemeColors } from '../context/ThemeContext';
import { isPublicPath } from '../utils/publicRoutes';

export default function ProfileGate({ children }) {
  const { user, userProfile, profileLoaded, profileError, retryProfile } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const { bg, card, text, sub, border } = useThemeColors();

  // El perfil NO SE PUDO LEER (red caída o API frío tras reintentar). No es lo mismo
  // que «no tiene cuenta»: mandarlo al selector de tipo de cuenta le haría creer que
  // la perdió. Solo un 404 real del perfil cuenta como sin cuenta (más abajo).
  if (!!user && profileLoaded && profileError && !userProfile && !isPublicPath(location.pathname)) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 380, width: '100%', textAlign: 'center', background: card, border: `1px solid ${border}`, borderRadius: 16, padding: 28 }}>
          <AlertTriangle size={36} color="#DC2626" />
          <h1 style={{ margin: '12px 0 6px', fontSize: 18, color: text }}>{t('auth.profileLoadFailedTitle')}</h1>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: sub }}>{t('auth.profileLoadFailedBody')}</p>
          <button
            onClick={retryProfile}
            style={{ padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', background: '#DC2626', color: '#fff', fontSize: 14, fontWeight: 600 }}
          >
            {t('errors.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Se espera a `profileLoaded` y NO a `userProfile != null`: quien SÍ tiene
  // cuenta también pasa por null mientras carga, y sin esa espera la guardia
  // echaría a todo el mundo a la pantalla de entrada en cada arranque.
  const sinCuenta = !!user && profileLoaded && !userProfile && !profileError;
  if (!sinCuenta) return children;

  // Las públicas siguen alcanzables: quien se quedó a medias tiene que poder
  // leer los términos que le piden aceptar.
  if (isPublicPath(location.pathname)) return children;

  return <Navigate to="/login" replace />;
}
